import { DEFAULT_VN_COUNTRY, isVietnamCountry, type VnAddress } from '@/shared/utils/vnAddress';

export interface PatientPlaceInput {
  isForeigner?: boolean;
  isVietKieu?: boolean;
  address?: VnAddress | null;
  residenceCountry?: string | null;
  residenceRegion?: string | null;
}

export interface PatientPlace {
  isForeigner: boolean;
  isVietKieu: boolean;
  livesAbroad: boolean;
  address: {
    street: string;
    ward: string;
    province: string;
    wardCode: string;
    provinceCode: string;
    country: string;
  };
  residenceCountry: string;
  residenceRegion: string;
}

/**
 * Keep in sync with backend `presentPatientPlace` in `normalizePatientForeigner.ts`.
 * Contact address is in Vietnam. Home country/state apply to Ngoại kiều and Việt kiều.
 * Older rows stored the home place in `address` when country was not Việt Nam.
 */
export const presentPatientPlace = (input: PatientPlaceInput): PatientPlace => {
  const isForeigner = Boolean(input.isForeigner);
  const isVietKieu = Boolean(input.isVietKieu);
  const livesAbroad = isForeigner || isVietKieu;
  const address = input.address ?? {};
  const storedCountry = address.country?.trim() || '';
  const legacyHome =
    livesAbroad &&
    Boolean(storedCountry) &&
    !isVietnamCountry(storedCountry) &&
    !input.residenceCountry?.trim();

  const residenceCountry = livesAbroad
    ? input.residenceCountry?.trim() || (legacyHome ? storedCountry : '')
    : '';
  const residenceRegion = livesAbroad
    ? input.residenceRegion?.trim() || (legacyHome ? address.province?.trim() || '' : '')
    : '';

  return {
    isForeigner,
    isVietKieu,
    livesAbroad,
    address: {
      street: legacyHome ? '' : address.street?.trim() || '',
      ward: legacyHome ? '' : address.ward?.trim() || '',
      province: legacyHome ? '' : address.province?.trim() || '',
      wardCode: legacyHome ? '' : address.wardCode?.trim() || '',
      provinceCode: legacyHome ? '' : address.provinceCode?.trim() || '',
      country: DEFAULT_VN_COUNTRY,
    },
    residenceCountry,
    residenceRegion,
  };
};
